import * as beautify from 'js-beautify';

import {
  EMPTY_EDITOR_HTML,
  SUPPORTED_SOURCE_LINK_PROTOCOLS,
} from './constants';

export function normalizeComparableHtml(value?: string) {
  const trimmed = value?.trim() ?? '';

  return trimmed === EMPTY_EDITOR_HTML ? '' : trimmed;
}

export function normalizeEmittedHtml(value?: string) {
  return normalizeComparableHtml(value);
}

export function escapeHtml(value: string) {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

function getExplicitHrefProtocol(value: string) {
  const trimmed = value.trim();
  const schemeDelimiterIndex = trimmed.indexOf(':');
  let protocol = '';

  if (trimmed && schemeDelimiterIndex !== -1) {
    const normalizedProtocol = `${Array.from(
      trimmed.slice(0, schemeDelimiterIndex)
    )
      .filter((character) => {
        const characterCode = character.charCodeAt(0);

        return characterCode > 0x20 && characterCode !== 0x7f;
      })
      .join('')
      .toLowerCase()}:`;

    if (/^[a-z][a-z0-9+.-]*:$/.test(normalizedProtocol)) {
      protocol = normalizedProtocol;
    }
  }

  return protocol;
}

function isUnsafeHref(value: string) {
  const protocol = getExplicitHrefProtocol(value);

  return Boolean(
    protocol && !SUPPORTED_SOURCE_LINK_PROTOCOLS.includes(protocol)
  );
}

export function removeUnsafeLinkHrefsFromDocument(document: Document) {
  document.body.querySelectorAll('a[href]').forEach((anchor) => {
    const href = anchor.getAttribute('href') ?? '';

    if (isUnsafeHref(href)) {
      anchor.replaceWith(...anchor.childNodes);
    }
  });
}

function removeUnsafeSourceElementsFromDocument(document: Document) {
  document.body
    .querySelectorAll(
      'script, iframe, object, embed, svg, style, link, meta, base'
    )
    .forEach((element) => {
      element.remove();
    });
}

function removeUnsafeSourceMediaFromDocument(document: Document) {
  document.body
    .querySelectorAll('img[src], video[src], audio[src], source[src]')
    .forEach((element) => {
      const src = element.getAttribute('src') ?? '';

      if (isUnsafeHref(src)) {
        element.remove();
      }
    });
}

function removeInlineEventHandlersFromDocument(document: Document) {
  document.body.querySelectorAll('*').forEach((element) => {
    Array.from(element.attributes).forEach((attribute) => {
      if (/^on/i.test(attribute.name)) {
        element.removeAttribute(attribute.name);
      }
    });
  });
}

export function removeUnsafeLinkHrefs(value: string) {
  if (!value.trim()) {
    return value;
  }

  const parser = new DOMParser();
  const document = parser.parseFromString(value, 'text/html');

  removeUnsafeLinkHrefsFromDocument(document);

  return document.body.innerHTML;
}

export function sanitizeSourceHtmlForEmission(value: string) {
  if (!value.trim()) {
    return value;
  }

  const parser = new DOMParser();
  const document = parser.parseFromString(value, 'text/html');

  removeUnsafeSourceElementsFromDocument(document);
  removeUnsafeLinkHrefsFromDocument(document);
  removeUnsafeSourceMediaFromDocument(document);
  removeInlineEventHandlersFromDocument(document);

  return document.body.innerHTML;
}

export function formatHtmlSource(value: string) {
  if (!value.trim()) {
    return value;
  }

  return beautify.html(value, {
    indent_size: 2,
    preserve_newlines: false,
    max_preserve_newlines: 0,
    wrap_line_length: 0,
    extra_liners: [],
    content_unformatted: ['pre', 'code', 'textarea'],
  });
}
